import { chakra } from '@chakra-ui/react'

import { PARAGRAPH_LINE_HEIGHT_PROPORTION } from 'src/components/constants'
import { scaleWithFont } from 'src/components/TranscriptViewer/constants'
import { shouldForwardProp } from 'src/utils/should-forward-prop'
import { isChromakeyModeActive } from 'src/utils/env'

import {
    INCONSISTENCY_INDICATOR_OFFSET,
    PARAGRAPH_HOVER_INSET_X,
    PARAGRAPH_HOVER_INSET_Y,
    PARAGRAPH_CONTAINER_DESKTOP_PADDING,
    PARAGRAPH_CONTAINER_MARGIN,
    PARAGRAPH_CONTAINER_MOBILE_PADDING,
    PARAGRAPH_METADATA_DESKTOP_MARGIN,
    PARAGRAPH_METADATA_MOBILE_MARGIN,
    SESSION_NAME_FONT_SIZE_PROPORTION,
    SESSION_NAME_FONT_WEIGHT,
    SESSION_NAME_LINE_HEIGHT_PROPORTION,
    SESSION_NAME_PADDING_BOTTOM,
    SESSION_NAME_PADDING_TOP,
} from './constants'

export const INCONSISTENCIES_CLASSNAME = 'paragraph-inconsistencies'

export const Wrapper = chakra('div', {
    baseStyle: {
        position: 'relative',
        transition: 'border-color 500ms ease-out',
    },
})

type ContainerProps = {
    $fontFamily: string
    $isFirst: boolean
    $shouldShift: boolean
    $maxWidth: number
    // CSS lengths (`useInconsistencyGutterCss`), so the room follows the live panel width.
    $inconsistencyGutter: { desktop: string; mobile: string }
}

const GUTTER_VAR = '--inconsistency-gutter'
const gutter = `var(${GUTTER_VAR})`
export const Container = chakra<'div', ContainerProps>('div', {
    baseStyle: ({ $fontFamily, $shouldShift, $maxWidth, $inconsistencyGutter, theme }) => ({
        position: 'relative',
        [GUTTER_VAR]: $inconsistencyGutter.desktop,
        display: 'flex',
        flexDirection: isChromakeyModeActive ? 'row' : 'column',
        alignItems: isChromakeyModeActive ? 'flex-start' : 'center',
        justifyContent: 'flex-start',
        width: '100%',
        maxWidth: `${$maxWidth}px`,
        padding: isChromakeyModeActive
            ? `0 ${PARAGRAPH_CONTAINER_DESKTOP_PADDING}px ${PARAGRAPH_CONTAINER_DESKTOP_PADDING}px`
            : `${PARAGRAPH_CONTAINER_DESKTOP_PADDING}px`,
        paddingLeft: isChromakeyModeActive ? undefined : `calc(${PARAGRAPH_CONTAINER_DESKTOP_PADDING}px + ${gutter})`,
        lineHeight: `${PARAGRAPH_LINE_HEIGHT_PROPORTION}em`,
        fontFamily: $fontFamily,
        margin: $shouldShift ? `0 ${PARAGRAPH_CONTAINER_MARGIN}px 0 auto` : '0 auto',
        borderRadius: '16px',
        _focusWithin: {
            boxShadow: `0 0 0 1px ${theme.palette.transcript.activeParagraphOutline}`,
        },
        // The outline marks the paragraph being edited; a focused chip is not editing. Without `:has()`
        // support only this rule drops, and the outline shows as before.
        [`&:has(.${INCONSISTENCIES_CLASSNAME}:focus-within)`]: {
            boxShadow: 'none',
        },
        // Hovering a paragraph with inconsistencies tints it and fans out its numbers (`data-group`). The tint starts
        // 16px left of the text, as in the design, so it never reaches the icon in the margin; the text's own
        // background goes see-through so the words are tinted too.
        // Its own stacking context keeps the tint (zIndex -1) behind the text. A row showing its numbers rises above
        // the next rows, which a tall number column can reach into.
        zIndex: 0,
        '&:hover, &:focus-within': { zIndex: 1 },
        // Its own rule: a browser without `:has()` drops a whole selector list that contains it.
        '&:has([data-expanded="true"])': { zIndex: 1 },
        '@media (hover: hover)': {
            '&[data-group]:hover::before': {
                content: '""',
                position: 'absolute',
                zIndex: -1,
                top: `${PARAGRAPH_CONTAINER_DESKTOP_PADDING - PARAGRAPH_HOVER_INSET_Y}px`,
                bottom: `${PARAGRAPH_CONTAINER_DESKTOP_PADDING - PARAGRAPH_HOVER_INSET_Y}px`,
                left: `calc(${PARAGRAPH_CONTAINER_DESKTOP_PADDING - PARAGRAPH_HOVER_INSET_X}px + ${gutter})`,
                right: `${PARAGRAPH_CONTAINER_DESKTOP_PADDING - PARAGRAPH_HOVER_INSET_X}px`,
                borderRadius: '12px',
                backgroundColor: theme.palette.inconsistencyIndicator.paragraphHoverBackgroundColor,
            },
            '&[data-group]:hover [data-paragraph-id]': {
                backgroundColor: 'transparent',
            },
        },

        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            '&': {
                width: `calc(100% - ${PARAGRAPH_CONTAINER_MARGIN * 2}px)`,
                [GUTTER_VAR]: $inconsistencyGutter.mobile,
                padding: `${PARAGRAPH_CONTAINER_MOBILE_PADDING}px`,
                paddingLeft: `calc(${PARAGRAPH_CONTAINER_MOBILE_PADDING}px + ${gutter})`,
            },
        },
    }),
    shouldForwardProp,
})

type MetadataProps = { $metadataWidth: number }
export const Metadata = chakra<'div', MetadataProps>('div', {
    baseStyle: ({ $metadataWidth, theme }) => ({
        position: 'relative',
        width: isChromakeyModeActive ? `${$metadataWidth}px` : '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '1em',
        fontFamily: 'inherit',
        marginBottom: isChromakeyModeActive ? 0 : `${PARAGRAPH_METADATA_DESKTOP_MARGIN}px`,
        marginRight: isChromakeyModeActive ? `${PARAGRAPH_METADATA_DESKTOP_MARGIN}px` : 0,

        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            '&': {
                gap: `0.5em`,
                marginBottom: isChromakeyModeActive ? 0 : `${PARAGRAPH_METADATA_MOBILE_MARGIN}px`,
            },
        },
    }),
    shouldForwardProp,
})

// The icon sits in the paragraph's left margin, centred on the header row (time and speaker).
export const InconsistenciesSlot = chakra('div', {
    baseStyle: {
        position: 'absolute',
        top: '50%',
        left: `calc(-1 * ${scaleWithFont(INCONSISTENCY_INDICATOR_OFFSET)})`,
        transform: 'translateY(-50%)',
        zIndex: 1,
    },
})

export const SessionTitle = chakra('h2', {
    baseStyle: {
        fontSize: `${SESSION_NAME_FONT_SIZE_PROPORTION}em`,
        lineHeight: `${SESSION_NAME_LINE_HEIGHT_PROPORTION}em`,
        fontWeight: SESSION_NAME_FONT_WEIGHT,
        padding: `${SESSION_NAME_PADDING_TOP}px 0 ${SESSION_NAME_PADDING_BOTTOM}px`,
        width: '100%',
    },
})
